import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTimelineCalendar } from '../models/use-timeline-calendar';
import { useTimelineCalendarInteractions } from './timeline-calendar/use-timeline-calendar-interactions';
import { useTimelineCalendarLayout } from './timeline-calendar/use-timeline-calendar-layout';
import { TimelineControls } from './timeline-calendar/sections/timeline-controls';
import { TimelineFloatingToolbar } from './timeline-calendar/sections/timeline-floating-toolbar';
import { TimelineMinimap } from './timeline-calendar/sections/timeline-minimap';
import { TimelineSurface } from './timeline-calendar/sections/timeline-surface';
import { FlatTimelineItem, TimelineCalendarProps } from './timeline-calendar/types';

export const TimelineCalendar = ({
  data,
  isReadOnly = false,
  showControls = true,
  showPeriodHeader = true,
  initialScale,
  initialViewRange,
  onViewChange,
  headerContent,
  aiPreviewItemStates,
  onSectionLabelChange,
  onSectionDelete,
  onSectionMove,
  onSectionVisibilityToggle,
  onItemTitleChange,
  onItemCreate,
  onItemDateRangeChange,
  onItemDelete,
}: TimelineCalendarProps) => {
  const flattenedSectionItems = useMemo<FlatTimelineItem[]>(
    () => data.pages[0].sections.flatMap((section, sectionIndex) => section.items.map((item) => ({
      ...item,
      sectionId: section.id,
      sectionLabel: section.label,
      sectionIndex,
    }))),
    [data.pages[0].sections],
  );

  const {
    selectedItemId,
    hoveredItemId,
    hoveredTooltip,
    dateRange,
    periodSegments,
    itemsWithColors,
    calculateItemPosition,
    shouldShowText,
    handleItemClick,
    handleItemHover,
    getRelativeFromStartLabel,
    getDurationLabel,
    currentConfig,
    movePeriod,
    resetToToday,
    setScale,
    fullDateRange,
    setViewRangeClamped,
    zoomByScale,
    panByMs,
  } = useTimelineCalendar({
    items: flattenedSectionItems,
    initialScale,
    initialViewRange,
    onViewChange,
  });

  const zoomSurfaceRef = useRef<HTMLDivElement>(null);
  const timelineContentRef = useRef<HTMLDivElement>(null);
  const timelineItemsRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);

  const [periodHeaderOffset, setPeriodHeaderOffset] = useState(0);
  const hiddenSectionIds = useMemo(
    () => new Set(data.pages[0].sections.filter(( section) => section.hidden).map(( section) => section.id)),
    [data.pages[0].sections],
  );

  const sectionMetaById = useMemo(
    () => new Map(data.pages[0].sections.map((section, sectionIndex) => [section.id, { section, sectionIndex }])),
    [data.pages[0].sections],
  );

  const sectionIndexById = useMemo(
    () => new Map(data.pages[0].sections.map((section, sectionIndex) => [section.id, { sectionIndex }])),
    [data.pages[0].sections],
  );

  const sectionItemById = useMemo(() => {
    const entries = itemsWithColors.flatMap((item) => {
      if (!item.sectionId) {
        return [];
      }
      const sectionMeta = sectionMetaById.get(item.sectionId);
      if (!sectionMeta) {
        return [];
      }
      const flatItem: FlatTimelineItem = {
        ...item,
        sectionId: item.sectionId,
        sectionLabel: sectionMeta.section.label,
        sectionIndex: sectionMeta.sectionIndex,
      };
      return [[item.id, flatItem] as const];
    });
    return new Map(entries);
  }, [itemsWithColors, sectionMetaById]);

  const interactions = useTimelineCalendarInteractions({
    isReadOnly,
    timelines: data.pages[0].sections,
    dateRange,
    zoomSurfaceRef,
    timelineItemsRef,
    sectionItemById,
    itemsWithColors,
    selectedItemId,
    handleItemClick,
    handleItemHover,
    zoomByScale,
    panByMs,
    onItemTitleChange,
    onItemCreate,
    onItemDateRangeChange,
  });

  const layout = useTimelineCalendarLayout({
    timelines: data.pages[0].sections,
    itemsWithColors,
    calculateItemPosition,
    shouldShowText,
    pendingCreateDraft: interactions.pendingCreateDraft,
    pendingCreateTitle: interactions.pendingCreateTitle,
    movePreviewState: interactions.movePreviewState,
    resizePreviewState: interactions.resizePreviewState,
    dragPreviewDraft: interactions.dragPreviewDraft,
    hoverPreviewDraft: interactions.hoverPreviewDraft,
    addMode: interactions.addMode,
    previewClientY: interactions.previewClientY,
    timelineItemsRef,
    sectionById: sectionIndexById,
    hiddenSectionIds,
  });

  const selectedItem = selectedItemId ? sectionItemById.get(selectedItemId) : undefined;
  const hoveredItem = hoveredTooltip?.itemId ? sectionItemById.get(hoveredTooltip.itemId) : undefined;
  const tooltipItem = hoveredItem ?? selectedItem;
  const durationLabel = tooltipItem
    ? getDurationLabel(tooltipItem.startDate, tooltipItem.endDate)
    : null;

  useLayoutEffect(() => {
    if (!showControls) {
      setPeriodHeaderOffset(0);
      return;
    }

    const updateOffset = () => {
      const height = controlsRef.current?.getBoundingClientRect().height ?? 0;
      setPeriodHeaderOffset(Math.round(height));
    };

    updateOffset();
    const target = controlsRef.current;
    if (!target) {
      return;
    }

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateOffset();
      });
      resizeObserver.observe(target);
    }

    window.addEventListener('resize', updateOffset);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateOffset);
    };
  }, [showControls]);

  useEffect(() => {
    const handleDeleteKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') {
        return;
      }

      if (isReadOnly) {
        return;
      }

      if (
        !selectedItemId
        || interactions.editingItemId
        || interactions.pendingCreateDraft
        || interactions.addMode !== 'none'
        || interactions.isResizeFlowActive
        || interactions.isMoveFlowActive
      ) {
        return;
      }

      const target = event.target as HTMLElement | null;
      if (
        target
        && (
          target.tagName === 'INPUT'
          || target.tagName === 'TEXTAREA'
          || target.isContentEditable
        )
      ) {
        return;
      }

      event.preventDefault();
      handleItemHover(null);
      const sectionId = sectionItemById.get(selectedItemId)?.sectionId;
      if (!sectionId) {
        return;
      }
      onItemDelete?.(sectionId, selectedItemId);
    };

    window.addEventListener('keydown', handleDeleteKeyDown);
    return () => window.removeEventListener('keydown', handleDeleteKeyDown);
  }, [
    interactions.addMode,
    interactions.editingItemId,
    interactions.isMoveFlowActive,
    interactions.isResizeFlowActive,
    interactions.pendingCreateDraft,
    isReadOnly,
    sectionItemById,
    onItemDelete,
    selectedItemId,
    handleItemHover,
  ]);

  return (
    <div className="flex min-h-full w-full flex-col">
      {showControls && (
        <TimelineControls
          controlsRef={controlsRef}
          headerContent={headerContent}
          dateRange={dateRange}
          currentConfig={currentConfig}
          setScale={setScale}
          movePeriod={movePeriod}
          resetToToday={resetToToday}
        />
      )}

      <TimelineSurface
        refs={{
          zoomSurface: zoomSurfaceRef,
          timelineContent: timelineContentRef,
          timelineItems: timelineItemsRef,
        }}
        aiPreviewItemStates={aiPreviewItemStates}
        calendar={{
          showPeriodHeader,
          periodHeaderOffset,
          horizontalScrollLeft: interactions.horizontalScrollLeft,
          periodSegments,
          dateRange,
        }}
        layout={{
          timelineMinHeight: layout.timelineMinHeight,
          shouldShowPreview: layout.shouldShowPreview,
          previewPosition: layout.previewPosition,
          previewTop: layout.previewTop,
          renderPreviewAsPoint: layout.renderPreviewAsPoint,
          isDurationCursorPreview: layout.isDurationCursorPreview,
          isMultiSection: layout.isMultiSection,
          multiSectionLayout: layout.multiSectionLayout,
          positionedRenderItems: layout.positionedRenderItems,
          shouldShowText: layout.shouldShowText,
          pendingItemId: layout.PENDING_ITEM_ID,
          pointLabelMaxWidthPx: layout.POINT_LABEL_MAX_WIDTH_PX,
        }}
        interaction={{
          readOnly: isReadOnly,
          pendingCreateTitle: interactions.pendingCreateTitle,
          editingItemId: interactions.editingItemId,
          editingTitleDraft: interactions.editingTitleDraft,
          setPendingCreateTitle: interactions.setPendingCreateTitle,
          setEditingTitleDraft: interactions.setEditingTitleDraft,
          startItemTitleEditing: interactions.startItemTitleEditing,
          handleItemClick,
          commitPendingCreate: interactions.commitPendingCreate,
          commitItemTitleEditing: interactions.commitItemTitleEditing,
          cancelPendingCreate: interactions.cancelPendingCreate,
          cancelItemTitleEditing: interactions.cancelItemTitleEditing,
          startMove: interactions.startMove,
          startResize: interactions.startResize,
          handleItemHover,
          isCreateFlowActive: interactions.isCreateFlowActive,
          isResizeFlowActive: interactions.isResizeFlowActive,
          isMoveFlowActive: interactions.isMoveFlowActive,
          pendingCreateDraft: interactions.pendingCreateDraft,
          resizePreviewState: interactions.resizePreviewState,
          movePreviewState: interactions.movePreviewState,
          addMode: interactions.addMode,
          dragPreviewDraft: interactions.dragPreviewDraft,
        }}
        events={{
          onScroll: interactions.handleZoomSurfaceScroll,
          onTouchStart: interactions.handleTouchStart,
          onTouchMove: interactions.handleTouchMove,
          onTouchEnd: interactions.handleTouchEnd,
          onWheel: interactions.handleWheel,
          onPointerDown: interactions.handleCreatePointerDown,
          onPointerMove: (event) => {
            if (interactions.applyResizePointerMove(event)) return;
            if (interactions.applyMovePointer(event)) return;
            interactions.handleCreatePointerMove(event);
          },
          onPointerUp: (event) => {
            if (interactions.commitResizePointer(event)) return;
            if (interactions.commitMovePointer(event)) return;
            interactions.finalizeCreatePointer(event);
          },
          onPointerCancel: (event) => {
            if (interactions.cancelResizePointer(event)) return;
            if (interactions.cancelMovePointer(event)) return;
            interactions.handleCreatePointerCancel(event);
          },
          onPointerEnter: interactions.handleCreatePointerEnter,
          onPointerLeave: interactions.handleCreatePointerLeave,
          onMouseMove: interactions.handleCreateMouseMove,
        }}
        sections={{
          timelines: data.pages[0].sections,
          onSectionLabelChange,
          onSectionDelete,
          onSectionMove,
          hiddenSectionIds,
          onSectionVisibilityToggle,
        }}
        selection={{
          hoveredItemId,
          selectedItemId,
          hoveredTooltip,
          tooltipItem,
          durationLabel,
          getRelativeFromStartLabel,
        }}
      />

      {!isReadOnly && showControls && (
        <TimelineFloatingToolbar
          addMode={interactions.addMode}
          handleAddModeClick={interactions.handleAddModeClick}
        />
      )}

      {showControls && (
        <TimelineMinimap
          fullDateRange={fullDateRange}
          dateRange={dateRange}
          itemsWithColors={itemsWithColors}
          hiddenSectionIds={hiddenSectionIds}
          onViewRangeChange={setViewRangeClamped}
          onZoom={zoomByScale}
        />
      )}
    </div>
  );
};
